import { useEffect, useRef, useState } from "react";

import { api, ApiError } from "../lib/api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TransButtons } from "../components/TransButtons";

/**
 * 招聘编辑器（独立 jobs 表，走 /api/admin/jobs CRUD）。
 * 列表本地编辑，「保存」时整列表同步：新增 POST / 已有 PUT / 删除 DELETE / 顺序 PATCH。
 */

type Bi = { cn: string; en: string };
type BiArr = { cn: string[]; en: string[] };
// 本地职位：id 为 null = 尚未持久化的新职位
type Job = {
  id: number | null;
  role: Bi; loc: Bi; type: Bi; exp: Bi;
  responsibilities: BiArr; skills: BiArr;
  active: boolean;
};

// 后端行 → 本地 Job
function fromRow(r: any): Job {
  return {
    id: Number(r.id),
    role: { cn: r.role_cn ?? "", en: r.role_en ?? "" },
    loc: { cn: r.loc_cn ?? "", en: r.loc_en ?? "" },
    type: { cn: r.type_cn ?? "", en: r.type_en ?? "" },
    exp: { cn: r.exp_cn ?? "", en: r.exp_en ?? "" },
    responsibilities: { cn: r.responsibilities?.cn ?? [], en: r.responsibilities?.en ?? [] },
    skills: { cn: r.skills?.cn ?? [], en: r.skills?.en ?? [] },
    active: r.active ?? true,
  };
}
// 本地 Job → 后端 body
function toBody(j: Job, sort_order: number) {
  return {
    role_cn: j.role.cn, role_en: j.role.en, loc_cn: j.loc.cn, loc_en: j.loc.en,
    type_cn: j.type.cn, type_en: j.type.en, exp_cn: j.exp.cn, exp_en: j.exp.en,
    responsibilities: j.responsibilities, skills: j.skills, sort_order, active: j.active,
  };
}

const EMPTY_JOB: Job = {
  id: null,
  role: { cn: "", en: "" }, loc: { cn: "深圳", en: "Shenzhen" },
  type: { cn: "全职", en: "Full-time" }, exp: { cn: "", en: "" },
  responsibilities: { cn: [], en: [] }, skills: { cn: [], en: [] }, active: true,
};

const toLines = (arr: string[]) => arr.join("\n");
const fromLines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function LinesPair({ label, cn, en, onCn, onEn }: {
  label: string; cn: string[]; en: string[]; onCn: (v: string[]) => void; onEn: (v: string[]) => void;
}) {
  const [cnText, setCnText] = useState(toLines(cn));
  const [enText, setEnText] = useState(toLines(en));
  useEffect(() => { setCnText(toLines(cn)); }, [cn]);
  useEffect(() => { setEnText(toLines(en)); }, [en]);
  return (
    <div className="je-lines">
      <div className="ce-label">{label} <span className="muted" style={{ textTransform: "none", letterSpacing: 0 }}>（每行一条）</span></div>
      <div className="ce-bilingual">
        <div className="ce-bi-col">
          <span className="ce-bi-tag">中</span>
          <textarea rows={5} value={cnText} onChange={(e) => setCnText(e.target.value)} onBlur={() => onCn(fromLines(cnText))} />
        </div>
        <div className="ce-bi-mid">
          <TransButtons getCn={() => cnText} getEn={() => enText}
            setCn={(v) => { setCnText(v); onCn(fromLines(v)); }} setEn={(v) => { setEnText(v); onEn(fromLines(v)); }} title={label} />
        </div>
        <div className="ce-bi-col">
          <span className="ce-bi-tag">EN</span>
          <textarea rows={5} value={enText} onChange={(e) => setEnText(e.target.value)} onBlur={() => onEn(fromLines(enText))} />
        </div>
      </div>
    </div>
  );
}

function BiInputs({ label, cn, en, onCn, onEn }: {
  label: string; cn: string; en: string; onCn: (v: string) => void; onEn: (v: string) => void;
}) {
  return (
    <div className="ce-field">
      <div className="ce-label">{label}</div>
      <div className="ce-bilingual">
        <div className="ce-bi-col"><span className="ce-bi-tag">中</span><input className="ce-input" value={cn} onChange={(e) => onCn(e.target.value)} /></div>
        <div className="ce-bi-mid"><TransButtons getCn={() => cn} getEn={() => en} setCn={onCn} setEn={onEn} title={label} /></div>
        <div className="ce-bi-col"><span className="ce-bi-tag">EN</span><input className="ce-input" value={en} onChange={(e) => onEn(e.target.value)} /></div>
      </div>
    </div>
  );
}

export function JobsEditorPage() {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [loadedIds, setLoadedIds] = useState<number[]>([]);   // 初始加载到的 id，用于算删除
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const listEnd = useRef<HTMLDivElement>(null);

  const load = () => {
    api.get<any[]>("/api/admin/jobs")
      .then((rows) => { const js = rows.map(fromRow); setJobs(js); setLoadedIds(js.map((j) => j.id!).filter(Boolean)); setDirty(false); })
      .catch((e) => setError(String(e)));
  };
  useEffect(load, []);

  const toggle = (i: number) => setExpanded((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const mutate = (next: Job[]) => { setJobs(next); setDirty(true); setSavedAt(null); };
  const updJob = (i: number, patch: Partial<Job>) => mutate(jobs!.map((it, x) => (x === i ? { ...it, ...patch } : it)));

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= jobs!.length) return;
    const copy = jobs!.slice(); [copy[i], copy[j]] = [copy[j], copy[i]]; mutate(copy);
  };

  const doRemove = (i: number) => {
    mutate(jobs!.filter((_, x) => x !== i));
    setExpanded((prev) => { const n = new Set<number>(); prev.forEach((x) => { if (x < i) n.add(x); else if (x > i) n.add(x - 1); }); return n; });
    setConfirmDel(null);
  };

  const add = () => {
    const newIdx = jobs!.length;
    mutate([...jobs!, JSON.parse(JSON.stringify(EMPTY_JOB))]);
    setExpanded((prev) => new Set(prev).add(newIdx));
    setTimeout(() => listEnd.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const save = async () => {
    if (!jobs) return;
    setSaving(true); setError(null);
    try {
      // 1) 删除：初始有、现在没了的 id
      const presentIds = new Set(jobs.map((j) => j.id).filter(Boolean) as number[]);
      for (const id of loadedIds) if (!presentIds.has(id)) await api.delete(`/api/admin/jobs/${id}`);
      // 2) 新增 / 更新（按当前顺序写 sort_order）
      const newJobs = [...jobs];
      for (let i = 0; i < newJobs.length; i++) {
        const j = newJobs[i];
        if (j.id == null) {
          const created = await api.post<any>("/api/admin/jobs", toBody(j, i));
          newJobs[i] = { ...j, id: Number(created.id) };
        } else {
          await api.put(`/api/admin/jobs/${j.id}`, toBody(j, i));
        }
      }
      setJobs(newJobs);
      setLoadedIds(newJobs.map((j) => j.id!).filter(Boolean));
      setDirty(false);
      setSavedAt(new Date().toLocaleTimeString("zh-CN"));
    } catch (e) {
      setError(e instanceof ApiError ? `保存失败 ${e.status}: ${JSON.stringify(e.body)}` : `保存失败：${String(e)}`);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">招聘</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>官网「招聘」页的在招职位，保存后约 1 分钟内生效</div>
        </div>
        <div className="page-actions" style={{ alignItems: "center", gap: 14 }}>
          {savedAt && !dirty && <span className="success" style={{ fontSize: 12 }}>已保存 {savedAt}</span>}
          {dirty && <span className="ce-dirty">有未保存修改</span>}
          <button className="btn btn-primary" onClick={add} disabled={!jobs}>+ 新增职位</button>
          <button className="btn" disabled={!dirty || saving} onClick={save}>{saving ? "保存中…" : "保存"}</button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 14 }}>{error}</div>}
      {!jobs && !error && <div className="muted">加载中…</div>}

      {jobs && (
        <div className="je-list">
          {jobs.map((job, i) => {
            const open = expanded.has(i);
            return (
            <div className={`je-card ${open ? "" : "collapsed"}`} key={job.id ?? `new-${i}`}>
              <div className="je-card-head je-card-head-toggle" onClick={() => toggle(i)}>
                <span className="ce-array-index">{String(i + 1).padStart(2, "0")}</span>
                <span className="je-card-title">{job.role.cn || "（未命名职位）"}</span>
                {!open && (job.loc.cn || job.type.cn || job.exp.cn) && (
                  <span className="je-card-summary">{[job.loc.cn, job.type.cn, job.exp.cn].filter(Boolean).join(" · ")}</span>
                )}
                <span className="ce-array-tools" onClick={(e) => e.stopPropagation()}>
                  <button className="ce-tool" title="上移" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button className="ce-tool" title="下移" disabled={i === jobs.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button className="ce-tool ce-tool-danger" title="删除" onClick={() => setConfirmDel(i)}>×</button>
                </span>
                <span className="je-card-caret" aria-hidden>{open ? "▾" : "▸"}</span>
              </div>
              {open && (
                <div className="je-card-body">
                  <BiInputs label="职位" cn={job.role.cn} en={job.role.en}
                    onCn={(v) => updJob(i, { role: { ...job.role, cn: v } })} onEn={(v) => updJob(i, { role: { ...job.role, en: v } })} />
                  <div className="je-meta-grid">
                    <BiInputs label="地点" cn={job.loc.cn} en={job.loc.en}
                      onCn={(v) => updJob(i, { loc: { ...job.loc, cn: v } })} onEn={(v) => updJob(i, { loc: { ...job.loc, en: v } })} />
                    <BiInputs label="类型" cn={job.type.cn} en={job.type.en}
                      onCn={(v) => updJob(i, { type: { ...job.type, cn: v } })} onEn={(v) => updJob(i, { type: { ...job.type, en: v } })} />
                    <BiInputs label="经验要求" cn={job.exp.cn} en={job.exp.en}
                      onCn={(v) => updJob(i, { exp: { ...job.exp, cn: v } })} onEn={(v) => updJob(i, { exp: { ...job.exp, en: v } })} />
                  </div>
                  <LinesPair label="职责" cn={job.responsibilities.cn} en={job.responsibilities.en}
                    onCn={(v) => updJob(i, { responsibilities: { ...job.responsibilities, cn: v } })}
                    onEn={(v) => updJob(i, { responsibilities: { ...job.responsibilities, en: v } })} />
                  <LinesPair label="要求" cn={job.skills.cn} en={job.skills.en}
                    onCn={(v) => updJob(i, { skills: { ...job.skills, cn: v } })}
                    onEn={(v) => updJob(i, { skills: { ...job.skills, en: v } })} />
                </div>
              )}
            </div>
            );
          })}
          {jobs.length === 0 && <div className="muted">暂无职位，点右上「+ 新增职位」添加</div>}
          <div ref={listEnd} />
        </div>
      )}

      <ConfirmDialog
        open={confirmDel !== null}
        title="删除职位"
        message={confirmDel !== null && <>确认删除「{jobs?.[confirmDel]?.role.cn || `第 ${confirmDel + 1} 个`}」职位？保存后对访客生效。</>}
        confirmLabel="删除" danger
        onConfirm={() => confirmDel !== null && doRemove(confirmDel)}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}
